import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import RestaurantMenuRoundedIcon from "@material-ui/icons/RestaurantMenuRounded";
import DateRangeRoundedIcon from "@material-ui/icons/DateRangeRounded";
import ScheduleRoundedIcon from "@material-ui/icons/ScheduleRounded";
import PageTitle from "../../components/PageTitle/PageTitle";
import SchoolFoodMenuService from "./Locality/Service/schoolFoodMenuService";

const useStyles = makeStyles((theme) => ({
  rootGrid: {
    alignItems: "stretch",
  },
  menuCard: {
    borderRadius: 14,
    border: "1px solid rgba(15, 59, 98, 0.12)",
    background: "linear-gradient(145deg, #ffffff 0%, #f6f9fd 100%)",
    boxShadow: "0 8px 28px rgba(15, 59, 98, 0.08)",
    height: "100%",
  },
  scheduleCard: {
    borderRadius: 14,
    border: "1px solid rgba(15, 59, 98, 0.12)",
    background: "#fff",
    boxShadow: "0 4px 16px rgba(15, 59, 98, 0.06)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  headingWrap: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "rgba(15, 59, 98, 0.1)",
    display: "grid",
    placeItems: "center",
    color: "#0f3b62",
  },
  menuName: {
    fontWeight: 700,
    color: "#203040",
    marginTop: theme.spacing(1.2),
    marginBottom: theme.spacing(1.8),
  },
  scheduleRow: {
    padding: theme.spacing(1.5, 0),
    borderBottom: "1px solid rgba(15, 59, 98, 0.08)",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  scheduleRowToday: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: 10,
    backgroundColor: "rgba(15, 59, 98, 0.05)",
    border: "1px solid rgba(15, 59, 98, 0.18)",
  },
  scheduleTitle: {
    fontWeight: 600,
    color: "#0f3b62",
    marginBottom: theme.spacing(0.5),
  },
  scheduleFood: {
    color: "#203040",
    marginBottom: theme.spacing(0.8),
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.2),
  },
  softChip: {
    backgroundColor: "rgba(15, 59, 98, 0.09)",
    color: "#0f3b62",
    fontWeight: 600,
  },
  todayChip: {
    backgroundColor: "rgba(67, 160, 71, 0.14)",
    color: "#2e7d32",
    fontWeight: 700,
  },
  helperText: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1.2),
  },
}));

const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function normalizeMenuItems(res) {
  if (Array.isArray(res)) return res;
  return res ? [res] : [];
}

function getTodayDayName() {
  return WEEKDAY_NAMES[new Date().getDay()];
}

function startOfDay(value) {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isDateInRange(today, fromDate, toDate) {
  const day = startOfDay(today);
  if (fromDate && day < startOfDay(fromDate)) return false;
  if (toDate && day > startOfDay(toDate)) return false;
  return true;
}

function itemHasDateRange(item) {
  return !!(item?.fromDate || item?.toDate);
}

function isMenuActiveToday(item) {
  const dayWise = !!item?.dayWise;
  const dateRange = itemHasDateRange(item);

  if (!dayWise && !dateRange) return true;

  if (dayWise && String(item?.dayName || "").trim() !== getTodayDayName()) return false;
  if (dateRange && !isDateInRange(new Date(), item.fromDate, item.toDate)) return false;

  return true;
}

function filterMenusForToday(items) {
  return items.filter(isMenuActiveToday);
}

function formatYmdToFriendly(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getItemFoodText(item) {
  const names = Array.isArray(item?.itemNames)
    ? item.itemNames.map((n) => String(n || "").trim()).filter(Boolean)
    : [];
  if (names.length) return names.join(", ");
  return String(item?.foodItemName || "").trim();
}

function getMenuRowLabel(item) {
  if (item?.dayWise && item?.dayName) return String(item.dayName).trim();
  if (itemHasDateRange(item)) {
    const from = formatYmdToFriendly(item.fromDate);
    const to = formatYmdToFriendly(item.toDate);
    if (from && to) return `${from} – ${to}`;
    if (from) return `From ${from}`;
    if (to) return `Until ${to}`;
    return "Date range";
  }
  if (item?.classWise) {
    return String(item?.classId?.className || "Class menu").trim();
  }
  return "General menu";
}

function sortMenusForDisplay(items) {
  return [...items].sort((a, b) => {
    const aDateOnly = itemHasDateRange(a) && !a?.dayWise;
    const bDateOnly = itemHasDateRange(b) && !b?.dayWise;
    if (aDateOnly && !bDateOnly) return 1;
    if (!aDateOnly && bDateOnly) return -1;

    if (a?.dayWise && b?.dayWise) {
      const ai = WEEKDAY_NAMES.indexOf(String(a.dayName || "").trim());
      const bi = WEEKDAY_NAMES.indexOf(String(b.dayName || "").trim());
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    }

    return new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
  });
}

function mergeMenuItems(items) {
  if (!items.length) return null;

  const nameSet = new Set();
  items.forEach((item) => {
    const text = getItemFoodText(item);
    if (text) {
      text.split(",").map((n) => n.trim()).filter(Boolean).forEach((n) => nameSet.add(n));
    }
  });

  const mergedNames = Array.from(nameSet);
  const primary =
    items.find((item) => item?.classWise && item?.classId) ||
    items.find((item) => item?.dayWise) ||
    items.find((item) => itemHasDateRange(item)) ||
    items[0];

  return {
    ...primary,
    itemNames: mergedNames,
    foodItemName: mergedNames.join(", "),
  };
}

function MenuScheduleRow({ item, classes }) {
  const today = isMenuActiveToday(item);
  const foodText = getItemFoodText(item);
  const label = getMenuRowLabel(item);
  const hasDateRange = itemHasDateRange(item);
  const hasDayWise = !!item?.dayWise;
  const hasClassWise = !!item?.classWise;
  const fromText = formatYmdToFriendly(item?.fromDate);
  const toText = formatYmdToFriendly(item?.toDate);
  const className = String(item?.classId?.className || "").trim();

  return (
    <Box className={today ? classes.scheduleRowToday : classes.scheduleRow}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
        <Typography variant="subtitle1" className={classes.scheduleTitle}>
          {label}
        </Typography>
        {today ? <Chip size="small" label="Today" className={classes.todayChip} /> : null}
      </Box>
      <Typography variant="body1" className={classes.scheduleFood}>
        {foodText || "—"}
      </Typography>
      <Box className={classes.chipRow} style={{ marginTop: 4 }}>
        {hasDayWise ? (
          <Chip
            size="small"
            label={`Day: ${item.dayName || "-"}`}
            icon={<ScheduleRoundedIcon style={{ color: "#0f3b62", fontSize: 16 }} />}
            className={classes.softChip}
          />
        ) : null}
        {hasDateRange ? (
          <Chip
            size="small"
            label={`${fromText || "Any"} → ${toText || "Any"}`}
            icon={<DateRangeRoundedIcon style={{ color: "#0f3b62", fontSize: 16 }} />}
            className={classes.softChip}
          />
        ) : null}
        {hasClassWise ? (
          <Chip size="small" label={`Class: ${className || "Particular class"}`} className={classes.softChip} />
        ) : null}
        {!hasDayWise && !hasDateRange && !hasClassWise ? (
          <Chip size="small" label="General" className={classes.softChip} />
        ) : null}
      </Box>
    </Box>
  );
}

export default function ParentFoodMenu() {
  const classes = useStyles();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let mounted = true;
    const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
    const classId = userDetails.classId?._id || userDetails.classId || "";
    SchoolFoodMenuService.getParentActive({
      moduleType: "MASTER",
      ...(classId ? { classId } : {}),
    })
      .then((res) => {
        if (!mounted) return;
        setMenuItems(sortMenusForDisplay(normalizeMenuItems(res)));
        setHasError(false);
      })
      .catch(() => {
        if (!mounted) return;
        setMenuItems([]);
        setHasError(true);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const todayMenus = filterMenusForToday(menuItems);
  const todayMerged = mergeMenuItems(todayMenus);
  const displayFoodText = todayMerged ? getItemFoodText(todayMerged) : "";
  const displayDate = new Date().toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const todayMenu = todayMenus[0];
  const hasTodayDateRange = todayMenu ? itemHasDateRange(todayMenu) : false;
  const hasTodayDayWise = todayMenu ? !!todayMenu.dayWise : false;
  const hasTodayClassWise = todayMenu ? !!todayMenu.classWise : false;
  const todayFromText = formatYmdToFriendly(todayMenu?.fromDate);
  const todayToText = formatYmdToFriendly(todayMenu?.toDate);
  const todayClassName = String(todayMenu?.classId?.className || "").trim();
  const todayPeriodText = hasTodayDateRange ? `${todayFromText || "Any"} → ${todayToText || "Any"}` : "";
  const todayPeriodLabel = hasTodayDateRange
    ? `Valid from ${todayFromText || "Any day"} to ${todayToText || "Any day"}`
    : hasTodayDayWise
    ? `Menu for ${getTodayDayName()}`
    : "Valid for all days";

  return (
    <>
      <PageTitle title="Food Menu" />
      <Grid container spacing={4} className={classes.rootGrid}>
        <Grid item xs={12} md={8}>
          <Card className={classes.menuCard}>
            <CardContent>
              <Box className={classes.cardHeader}>
                <Box className={classes.headingWrap}>
                  <Box className={classes.iconWrap}>
                    <RestaurantMenuRoundedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="h6">Today&apos;s Food Menu</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Healthy meals curated for children
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={displayDate}
                  icon={<DateRangeRoundedIcon style={{ color: "#0f3b62" }} />}
                  className={classes.softChip}
                />
              </Box>
              <Divider style={{ margin: "12px 0 14px" }} />
              {loading ? (
                <Typography variant="body2" className={classes.helperText}>
                  Loading today&apos;s menu...
                </Typography>
              ) : hasError ? (
                <Typography variant="body2" className={classes.helperText}>
                  Unable to load menu right now. Please try again in a moment.
                </Typography>
              ) : !displayFoodText ? (
                <Typography variant="body2" className={classes.helperText}>
                  No menu scheduled for {getTodayDayName()} today. See the weekly schedule below.
                </Typography>
              ) : (
                <Box>
                  <Typography variant="h5" className={classes.menuName}>
                    {displayFoodText}
                  </Typography>
                  <Box className={classes.chipRow}>
                    <Chip label={todayPeriodLabel} className={classes.softChip} />
                    {hasTodayDateRange ? (
                      <Chip label={`Range: ${todayPeriodText}`} className={classes.softChip} />
                    ) : null}
                    {hasTodayDayWise ? (
                      <Chip
                        label={`Day wise: ${getTodayDayName()}`}
                        icon={<ScheduleRoundedIcon style={{ color: "#0f3b62" }} />}
                        className={classes.softChip}
                      />
                    ) : null}
                    {hasTodayClassWise ? (
                      <Chip
                        label={`Class: ${todayClassName || "Particular class"}`}
                        className={classes.softChip}
                      />
                    ) : null}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card className={classes.scheduleCard}>
            <CardContent>
              <Box className={classes.headingWrap} style={{ marginBottom: 12 }}>
                <Box className={classes.iconWrap}>
                  <ScheduleRoundedIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h6">Food Menu Schedule</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Day-wise and date-range menus from your school
                  </Typography>
                </Box>
              </Box>
              <Divider style={{ marginBottom: 8 }} />
              {loading ? (
                <Typography variant="body2" className={classes.helperText}>
                  Loading schedule...
                </Typography>
              ) : menuItems.length === 0 ? (
                <Typography variant="body2" className={classes.helperText}>
                  {hasError ? "Unable to load menus." : "No active menus found."}
                </Typography>
              ) : (
                menuItems.map((item) => (
                  <MenuScheduleRow key={item._id} item={item} classes={classes} />
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
